/**
 * 前台布局 (所有登录用户): 报价 / 画图 / 模板 / 记录 (+ 普通用户的「我的用量」) / 账号设置。
 *
 * 桌面: 绿色渐变顶栏 (Logo + 产品名 / 余额 · 经典版 · 管理后台 · 用户菜单) + 下方白色等宽 tab 栏, 配色同旧版。
 * 手机: 绿色精简顶栏 (Logo · 余额 · 头像菜单) + 底部固定 tab 栏 (带 safe-area 留白), 不用抽屉。
 *
 * 整体固定为可视区高度, 由 main 内部滚动: 聊天页的输入框始终贴在底部 (tab 栏上方)。
 * 既可作为布局路由 (渲染 <Outlet/>), 也可直接包 children。
 */
import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { ArrowLeftRight, BarChart3, FileText, History, Image as ImageIcon, LayoutDashboard, MessageSquareText } from "lucide-react";
import { NavTabs, type NavTabItem } from "@/components/ui/Tabs";
import { LEGACY_HOME, homePathFor } from "@/features/auth/utils";
import { cn } from "@/lib/utils";
import { BalanceChip } from "./BalanceChip";
import { BrandMark } from "./BrandMark";
import { UserMenu, type UserMenuLink } from "./UserMenu";
import { useShell } from "./useShell";

const TABS: Array<NavTabItem & { icon: NonNullable<NavTabItem["icon"]>; userOnly?: boolean }> = [
  { to: "/", label: "报价", icon: MessageSquareText, end: true },
  { to: "/image", label: "画图", icon: ImageIcon },
  { to: "/template", label: "模板", icon: FileText },
  { to: "/history", label: "记录", icon: History },
  // 普通用户才有; 管理员在后台看完整的成本统计
  { to: "/cost", label: "我的用量", icon: BarChart3, userOnly: true },
];

/** 手机上聚焦输入框 (软键盘弹出) 时收起底部 tab 栏, 给输入框腾位置 */
function useTypingOnMobile(): boolean {
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const isText = (el: EventTarget | null) =>
      el instanceof HTMLTextAreaElement ||
      (el instanceof HTMLInputElement && !["checkbox", "radio", "button", "submit", "range", "file"].includes(el.type));
    const onIn = (e: FocusEvent) => setTyping(isText(e.target));
    const onOut = () => setTyping(false);
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);
  return typing;
}

export function UserLayout({ children }: { children?: ReactNode }) {
  const { user, isAdmin } = useShell();
  const typing = useTypingOnMobile();
  const tabs = TABS.filter((t) => !t.userOnly || !isAdmin);
  const adminHome = user ? homePathFor(user.role) : "/dashboard";

  // 手机底部只放 4 个主 tab; 其余入口放进头像菜单
  const mobileTabs = tabs.filter((t) => !t.userOnly);
  const menuLinks: UserMenuLink[] = [
    ...tabs.filter((t) => t.userOnly).map((t) => ({ to: t.to, label: String(t.label), icon: t.icon })),
    ...(isAdmin ? [{ to: adminHome, label: "管理后台", icon: LayoutDashboard }] : []),
    // 旧版前台 (在 SPA 之外, 整页跳转)
    { to: LEGACY_HOME, label: "经典版", icon: ArrowLeftRight, external: true },
  ];

  const pillCls =
    "hidden h-8 items-center gap-1.5 rounded-full border border-white/30 bg-white/15 px-3 text-sm font-medium text-white transition-colors hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 md:inline-flex [&_svg]:size-4";

  return (
    <div className="flex h-[100dvh] flex-col bg-background">
      {/* 旧版同款: 绿色渐变顶栏 (白字) + 下方白色等宽 tab 栏 (绿色下划线) */}
      <header className="z-30 shrink-0 bg-gradient-to-br from-brand to-[#43a047] pt-[env(safe-area-inset-top)] shadow-[0_2px_8px_rgba(56,142,60,0.25)]">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4 md:gap-4">
          <BrandMark inverse className="min-w-0" />
          <div className="flex-1" />
          <BalanceChip inverse />
          <a href={LEGACY_HOME} className={pillCls} title="切换到经典版页面">
            <ArrowLeftRight aria-hidden />
            经典版
          </a>
          {isAdmin && (
            <Link to={adminHome} className={pillCls}>
              <LayoutDashboard aria-hidden />
              管理后台
            </Link>
          )}
          <UserMenu inverse links={menuLinks} />
        </div>
      </header>
      <div className="z-20 hidden shrink-0 border-b bg-card md:block">
        <NavTabs
          items={tabs}
          aria-label="主导航"
          className="mx-auto h-12 max-w-6xl gap-0 px-4"
          itemClassName="flex-1 justify-center text-[15px] hover:bg-muted/60"
        />
      </div>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto h-full w-full max-w-6xl">{children ?? <Outlet />}</div>
      </main>

      <nav
        aria-label="主导航"
        className={cn("shrink-0 border-t bg-card pb-safe md:hidden", typing && "hidden")}
      >
        <div className="grid grid-cols-4">
          {mobileTabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                cn(
                  "flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                  isActive ? "text-primary" : "text-muted-foreground active:text-foreground",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "flex h-7 w-12 items-center justify-center rounded-full transition-colors",
                      isActive && "bg-primary-soft",
                    )}
                  >
                    <t.icon aria-hidden className="h-5 w-5" strokeWidth={isActive ? 2.25 : 2} />
                  </span>
                  {t.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
